import apiClient from './client';
import { CrawlResponse } from '../types';
import {
  normalizeDate,
  validateSport,
  validateAIProvider,
  sanitizeParam
} from './validation';

// ===== Crawl API =====

// Crawl multiple sports sequentially (group crawl)
export const crawlSportsGroup = async (
  sports: string[],
  date?: string,
  aiProvider?: string
): Promise<CrawlResponse> => {
  try {
    const params = new URLSearchParams({
      sports: sports.join(',')
    });

    if (date) {
      params.append('date', normalizeDate(date));
    }

    if (aiProvider) {
      params.append('ai_provider', aiProvider);
    }

    const response = await apiClient.get(`/api/crawl/group?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    console.error('[Group crawl error]', error);

    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }

    throw new Error(error.message || 'Failed to crawl sports group');
  }
};

// Crawl all sports by date (or date range)
export const crawlByDate = async (
  date: string,
  aiProvider?: string,
  endDate?: string,
  adminId?: number,
  notionAdminId?: number
): Promise<CrawlResponse> => {
  try {
    // Validate date
    const normalizedDate = normalizeDate(date);

    const params = new URLSearchParams({
      date: normalizedDate
    });

    if (endDate) {
      params.append('end_date', normalizeDate(endDate));
    }

    if (aiProvider) {
      params.append('ai_provider', aiProvider);
    }

    if (adminId) {
      params.append('admin_id', adminId.toString());
    }

    if (notionAdminId) {
      params.append('notion_admin_id', notionAdminId.toString());
    }

    const response = await apiClient.get(`/api/crawl/by-date?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    console.error('[Crawl by date error]', error);

    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Re-throw with proper message
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }

    throw new Error(error.message || 'Failed to crawl matches by date');
  }
};

// Crawl API
export const crawlMatches = async (
  sport: string,
  league?: string,
  date?: string,
  aiProvider?: string,
  adminId?: number,
  notionAdminId?: number
): Promise<CrawlResponse> => {
  try {
    // Validate inputs before making API call
    const validatedSport = validateSport(sport);

    const params = new URLSearchParams({
      sport: validatedSport
    });

    if (aiProvider) {
      const validatedProvider = validateAIProvider(aiProvider);
      params.append('ai_provider', validatedProvider);
    }

    if (league && league.trim()) {
      params.append('league', sanitizeParam(league) || '');
    }

    if (date) {
      params.append('date', normalizeDate(date));
    }

    if (adminId) {
      params.append('admin_id', adminId.toString());
    }

    if (notionAdminId) {
      params.append('notion_admin_id', notionAdminId.toString());
    }

    const url = `/api/crawl?${params}`;
    console.log('Crawl API URL:', url);
    console.log('Crawl API params:', Object.fromEntries(params));

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Crawl API error:', error);
    console.error('Error response:', error.response);

    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Handle API errors with more detail
    if (error.response) {
      const detail = error.response.data?.detail || error.response.data?.message || 'Unknown error';
      throw new Error(`Server error (${error.response.status}): ${detail}`);
    }

    // Re-throw other errors
    throw error;
  }
};

export const crawlMatchesPartial = async (
  sport: string,
  league?: string,
  date?: string,
  aiProvider?: string,
  adminId?: number,
  notionAdminId?: number
): Promise<CrawlResponse> => {
  try {
    // Validate inputs
    const validatedSport = validateSport(sport);

    const params = new URLSearchParams({
      sport: validatedSport
    });

    if (aiProvider) {
      const validatedProvider = validateAIProvider(aiProvider);
      params.append('ai_provider', validatedProvider);
    }

    if (league && league.trim()) {
      params.append('league', sanitizeParam(league) || '');
    }

    if (date) {
      params.append('date', normalizeDate(date));
    }

    if (adminId) {
      params.append('admin_id', adminId.toString());
    }

    if (notionAdminId) {
      params.append('notion_admin_id', notionAdminId.toString());
    }

    const url = `/api/crawl/part?${params}`;
    console.log('Crawl Partial API URL:', url);
    console.log('Crawl Partial API params:', Object.fromEntries(params));

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Crawl Partial API error:', error);
    console.error('Error response:', error.response);

    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Handle API errors with more detail
    if (error.response) {
      const detail = error.response.data?.detail || error.response.data?.message || 'Unknown error';
      throw new Error(`Server error (${error.response.status}): ${detail}`);
    }

    // Re-throw other errors
    throw error;
  }
};
